import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Upload, Check, X } from "lucide-react";
import { API, useApp } from "../context/AppContext";
import ImageOrArt from "../components/ImageOrArt";

const TABS = ["Overview", "Site Mode", "Artworks", "Products", "Exhibitions", "News", "Media Kit", "Messages", "Waitlist", "Orders", "Newsletter"];

const SITE_MODES = [
  { key: "live", label: "The Gallery Is Open", desc: "The full site is live — every page, the shop, everything. This is the default state." },
  { key: "soon", label: "Something Bold Is Loading", desc: "Pre-launch. Every public page (including the shop) shows a branded holding page with email capture. You still see the full site while signed in." },
  { key: "studio", label: "Back In The Studio", desc: "Upgrade mode. The public sees a branded maintenance page with email capture. You still see the full site while signed in." },
];

function SiteModeSection() {
  const { siteMode, loadSiteMode } = useApp();
  const [busy, setBusy] = useState("");
  const setMode = async (mode) => {
    setBusy(mode);
    try {
      await axios.put(`${API}/admin/site-mode`, { mode }, { withCredentials: true });
      await loadSiteMode();
      toast.success(`Site mode: ${SITE_MODES.find((m) => m.key === mode).label}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border" data-testid="site-mode-switch">
      {SITE_MODES.map((m) => (
        <button
          key={m.key}
          onClick={() => setMode(m.key)}
          disabled={busy === m.key}
          data-testid={`site-mode-${m.key}`}
          className={`p-8 text-left transition-colors duration-300 ${siteMode === m.key ? "bg-[#FF1A1A] text-white" : "bg-card hover:bg-secondary/60"}`}
        >
          <span className={`font-mono-x text-[10px] uppercase tracking-widest ${siteMode === m.key ? "text-white/80" : "text-[#FF1A1A]"}`}>
            {siteMode === m.key ? "Active" : busy === m.key ? "Switching…" : "Switch"}
          </span>
          <span className="font-display font-black uppercase text-xl block mt-3">{m.label}</span>
          <span className={`text-sm mt-3 block ${siteMode === m.key ? "text-white/85" : "text-muted-foreground"}`}>{m.desc}</span>
        </button>
      ))}
    </div>
  );
}

const CONFIG = {
  Artworks: {
    coll: "artworks",
    label: (r) => r.title,
    sub: (r) => `${r.year || ""} — ${r.category || ""} — $${Number(r.price || 0).toLocaleString()} — ${r.availability || ""}`,
    fields: [
      { n: "title", l: "Title" },
      { n: "year", l: "Year", t: "number" },
      { n: "medium", l: "Medium" },
      { n: "dimensions", l: "Dimensions" },
      { n: "price", l: "Price (USD)", t: "number" },
      { n: "availability", l: "Availability", t: "select", opts: ["Available", "Collected", "On Hold", "Not For Sale"] },
      { n: "category", l: "Category", t: "select", opts: ["Field Faces", "Flags", "Mixed Media", "Works on Paper", "Special Projects"] },
      { n: "featured", l: "Featured on homepage", t: "check" },
      { n: "blurb", l: "Description", t: "textarea" },
    ],
    image: true,
    seedPrefix: "aw",
  },
  Products: {
    coll: "products",
    label: (r) => r.name,
    sub: (r) => `$${Number(r.price || 0).toLocaleString()} — ${r.category || ""} — ${r.collection || ""}`,
    fields: [
      { n: "name", l: "Name" },
      { n: "price", l: "Price (USD)", t: "number" },
      { n: "category", l: "Category", t: "select", opts: ["Original Artwork", "Prints", "Apparel", "Accessories", "RISE Marketplace"] },
      { n: "collection", l: "Collection", t: "select", opts: ["Field Faces Collection", "Peace Love Power Collection", "RISE Collection"] },
      { n: "badges", l: "Badges", hint: "Comma separated: New Arrival, Collector Favorite, Limited Edition" },
      { n: "desc", l: "Description", t: "textarea" },
    ],
    image: true,
    seedPrefix: "pr",
  },
  Exhibitions: {
    coll: "exhibitions",
    label: (r) => r.title,
    sub: (r) => `${r.venue || ""} — ${r.years || ""} — ${r.status || ""}`,
    fields: [
      { n: "title", l: "Title" },
      { n: "venue", l: "Venue" },
      { n: "years", l: "Years (e.g. 2025, 2026)" },
      { n: "type", l: "Type", t: "select", opts: ["Solo", "Group", "Fundraiser", "Public"] },
      { n: "status", l: "Status", t: "select", opts: ["Featured", "Archive"], hint: "Setting Featured removes it from the others" },
      { n: "description", l: "Description", t: "textarea" },
    ],
    image: true,
    seedPrefix: "ex",
  },
  News: {
    coll: "news",
    label: (r) => r.title,
    sub: (r) => `${r.category || ""} — ${r.date || ""}${r.featured ? " — FEATURED" : ""}`,
    fields: [
      { n: "title", l: "Title" },
      { n: "category", l: "Category", t: "select", opts: ["Press", "Exhibitions", "Studio Journal", "Curatorial Notes", "DATOS Updates"] },
      { n: "date", l: "Date (YYYY-MM-DD)" },
      { n: "source", l: "Source" },
      { n: "featured", l: "Featured story", t: "check" },
      { n: "excerpt", l: "Excerpt", t: "textarea" },
      { n: "body", l: "Body", t: "textarea" },
    ],
    image: true,
    seedPrefix: "nw",
  },
};

function formatError(e) {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return "Something went wrong.";
}

function ImageUpload({ value, seed, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      onChange(data.path);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={testid}>
      <div className="w-32 border border-border overflow-hidden mb-2">
        <ImageOrArt image={value} seed={seed} ratio="1/1" className="w-full" />
      </div>
      <label className={`btn-ghost !py-2 !px-4 text-[10px] inline-flex cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid={`${testid}-button`}>
        <Upload size={12} /> {busy ? "Uploading…" : value ? "Replace Photo" : "Upload Photo"}
        <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      </label>
      {value && (
        <button type="button" onClick={() => onChange("")} className="ml-3 text-[10px] font-mono-x uppercase tracking-widest text-muted-foreground hover:text-[#FF1A1A]" data-testid={`${testid}-remove`}>
          Remove photo
        </button>
      )}
    </div>
  );
}

function CrudSection({ cfg, tab }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/${cfg.coll}`, { withCredentials: true });
      setRows(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  useEffect(() => {
    load();
  }, [tab]);

  const startCreate = () => setForm({ seed: `${cfg.seedPrefix}-${Date.now()}` });
  const startEdit = (r) => setForm({ ...r, badges: Array.isArray(r.badges) ? r.badges.join(", ") : r.badges || "" });

  const save = async () => {
    const payload = { ...form };
    delete payload._id;
    delete payload.created_at;
    for (const f of cfg.fields) {
      if (f.t === "number") payload[f.n] = Number(payload[f.n]) || 0;
    }
    if (cfg.coll === "products") payload.badges = String(payload.badges || "").split(",").map((b) => b.trim()).filter(Boolean);
    try {
      if (payload.id) {
        const id = payload.id;
        delete payload.id;
        await axios.put(`${API}/admin/${cfg.coll}/${id}`, payload, { withCredentials: true });
        toast.success("Saved.");
      } else {
        delete payload.id;
        await axios.post(`${API}/admin/${cfg.coll}`, payload, { withCredentials: true });
        toast.success("Created.");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/${cfg.coll}/${id}`, { withCredentials: true });
      toast.success("Deleted.");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div data-testid={`admin-section-${cfg.coll}`}>
      {!form ? (
        <button onClick={startCreate} className="btn-red !py-3 text-xs mb-8" data-testid={`admin-new-${cfg.coll}`}>
          + New {cfg.coll.slice(0, -1)}
        </button>
      ) : (
        <div className="card-line p-6 mb-10 space-y-4" data-testid={`admin-form-${cfg.coll}`}>
          <div className="flex justify-between items-center">
            <p className="font-display font-bold uppercase text-sm">{form.id ? "Edit" : "New"} {cfg.coll.slice(0, -1)}</p>
            <button onClick={() => setForm(null)} className="text-muted-foreground hover:text-[#FF1A1A]" aria-label="Cancel" data-testid={`admin-form-cancel-${cfg.coll}`}>
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cfg.fields.map((f) => (
              <div key={f.n} className={f.t === "textarea" ? "sm:col-span-2" : ""}>
                <label className="font-mono-x text-[10px] uppercase tracking-widest text-muted-foreground block mb-1.5">{f.l}</label>
                {f.t === "select" ? (
                  <select className="input-x" value={form[f.n] || f.opts[0]} onChange={(e) => setForm({ ...form, [f.n]: e.target.value })} data-testid={`admin-field-${f.n}`}>
                    {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.t === "textarea" ? (
                  <textarea className="input-x min-h-[100px]" value={form[f.n] || ""} onChange={(e) => setForm({ ...form, [f.n]: e.target.value })} data-testid={`admin-field-${f.n}`} />
                ) : f.t === "check" ? (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!form[f.n]} onChange={(e) => setForm({ ...form, [f.n]: e.target.checked })} data-testid={`admin-field-${f.n}`} className="accent-[#FF1A1A] w-4 h-4" />
                    {f.l}
                  </label>
                ) : (
                  <input className="input-x" type={f.t === "number" ? "number" : "text"} value={form[f.n] ?? ""} onChange={(e) => setForm({ ...form, [f.n]: e.target.value })} data-testid={`admin-field-${f.n}`} />
                )}
                {f.hint && <p className="text-[10px] text-muted-foreground mt-1">{f.hint}</p>}
              </div>
            ))}
          </div>
          {cfg.image && (
            <ImageUpload value={form.image} seed={form.seed} onChange={(p) => setForm({ ...form, image: p })} testid={`admin-image-${cfg.coll}`} />
          )}
          <button onClick={save} className="btn-red !py-3 text-xs" data-testid={`admin-form-save-${cfg.coll}`}>
            {form.id ? "Save Changes" : "Create"}
          </button>
          {cfg.coll === "products" && <p className="text-[10px] text-muted-foreground font-mono-x uppercase tracking-widest">Price changes sync to Stripe automatically</p>}
        </div>
      )}
      {!rows ? (
        <p className="font-mono-x text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-px bg-border border border-border" data-testid={`admin-list-${cfg.coll}`}>
          {rows.length === 0 && <p className="bg-card p-6 text-sm text-muted-foreground">Nothing here yet.</p>}
          {rows.map((r) => (
            <div key={r.id} className="bg-card p-4 flex items-center gap-4 text-sm" data-testid={`admin-row-${r.id}`}>
              {cfg.image && (
                <div className="w-12 h-12 shrink-0 border border-border overflow-hidden">
                  <ImageOrArt image={r.image} seed={r.seed} ratio="1/1" className="w-full h-full" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-display font-bold uppercase truncate">{cfg.label(r)}</p>
                <p className="font-mono-x text-[10px] text-muted-foreground mt-1 truncate">{cfg.sub(r)}</p>
              </div>
              <button onClick={() => startEdit(r)} className="text-muted-foreground hover:text-[#FF1A1A] transition-colors" aria-label="Edit" data-testid={`admin-edit-${r.id}`}>
                <Pencil size={16} />
              </button>
              <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-[#FF1A1A] transition-colors" aria-label="Delete" data-testid={`admin-delete-${r.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MediaKitSection() {
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => {
    const { data } = await axios.get(`${API}/mediakit`);
    setDocs(data);
  };
  useEffect(() => {
    load();
  }, []);
  const upload = async (slug, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/mediakit/${slug}`, fd, { withCredentials: true });
      toast.success("Document replaced.");
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="space-y-px bg-border border border-border" data-testid="admin-mediakit">
      {(docs || []).map((d) => (
        <div key={d.slug} className="bg-card p-5 flex flex-wrap items-center gap-4 justify-between text-sm" data-testid={`mediakit-row-${d.slug}`}>
          <div>
            <p className="font-display font-bold uppercase">{d.title}</p>
            <p className="font-mono-x text-[10px] text-muted-foreground mt-1">{d.custom ? "Custom file active" : "Placeholder PDF active"}</p>
          </div>
          <div className="flex items-center gap-3">
            {d.custom && <Check size={14} className="text-[#FF1A1A]" />}
            <label className={`btn-ghost !py-2 !px-4 text-[10px] cursor-pointer ${busy === d.slug ? "opacity-50 pointer-events-none" : ""}`} data-testid={`mediakit-upload-${d.slug}`}>
              <Upload size={12} /> {busy === d.slug ? "Uploading…" : "Upload PDF"}
              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(d.slug, e)} />
            </label>
            <a href={`${API}/mediakit/${d.slug}`} className="text-[10px] font-mono-x uppercase tracking-widest text-muted-foreground hover:text-[#FF1A1A]" data-testid={`mediakit-download-${d.slug}`}>
              Download
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

const LIST_TABS = { Messages: "contacts", Waitlist: "waitlist", Orders: "payment_transactions", Newsletter: "newsletter" };

function SimpleList({ tab }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/admin/${LIST_TABS[tab]}`, { withCredentials: true })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatError(e)));
  }, [tab]);
  if (!rows) return <p className="font-mono-x text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-px bg-border border border-border" data-testid={`admin-list-${tab.toLowerCase()}`}>
      {rows.length === 0 && <p className="bg-card p-6 text-sm text-muted-foreground">Nothing here yet.</p>}
      {rows.map((r, i) => (
        <div key={r.id || r.session_id || i} className="bg-card p-5 text-sm" data-testid={`admin-row-${r.id || r.session_id || i}`}>
          <p className="font-display font-bold uppercase">{r.title || r.name || r.email || r.session_id}</p>
          <p className="font-mono-x text-[10px] text-muted-foreground mt-1">
            {r.category || r.role || r.status || ""} {r.created_at || ""} {r.amount != null ? `— $${(r.amount / 100).toLocaleString()} ${r.payment_status}` : ""}
          </p>
          {r.message && <p className="text-xs text-muted-foreground mt-2 max-w-xl">{r.message}</p>}
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, setAdmin, checkAdmin } = useApp();
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("Overview");
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (admin === null) checkAdmin();
  }, []);

  useEffect(() => {
    if (admin && tab === "Overview") {
      axios.get(`${API}/admin/overview`, { withCredentials: true }).then(({ data }) => setOverview(data)).catch(() => {});
    }
  }, [admin, tab]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      setAdmin(data);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (admin === null) return <main className="pt-40 container-x"><p className="font-mono-x text-sm text-muted-foreground">Loading…</p></main>;

  if (!admin)
    return (
      <main className="pt-24 sm:pt-32" data-testid="admin-login-page">
        <section className="container-x section-y max-w-md">
          <p className="eyebrow mb-6">Admin CMS</p>
          <h1 className="h-section mb-10">Sign In</h1>
          <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
            <input className="input-x" type="email" required placeholder="Email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} data-testid="admin-email" aria-label="Admin email" />
            <input className="input-x" type="password" required placeholder="Password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} data-testid="admin-password" aria-label="Admin password" />
            <button type="submit" className="btn-red w-full" data-testid="admin-cms-login-btn">Sign In</button>
          </form>
        </section>
      </main>
    );

  return (
    <main className="pt-24 sm:pt-32" data-testid="admin-dashboard">
      <section className="container-x section-y">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-3">Admin CMS</p>
            <h1 className="h-section">Studio Dashboard</h1>
          </div>
          <button
            onClick={async () => { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); setAdmin(false); }}
            className="btn-ghost !py-2.5 !px-5 text-xs"
            data-testid="admin-logout"
          >
            Sign Out
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-12" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-display font-bold uppercase tracking-widest text-xs px-4 py-2 border transition-colors ${tab === t ? "bg-[#FF1A1A] border-[#FF1A1A] text-white" : "border-border hover:border-[#FF1A1A]"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border" data-testid="admin-overview-grid">
            {Object.entries(overview).map(([k, v]) => (
              <div key={k} className="bg-card p-6">
                <p className="font-display font-black text-3xl text-[#FF1A1A]">{v}</p>
                <p className="font-mono-x text-[10px] uppercase tracking-widest text-muted-foreground mt-2">{k}</p>
              </div>
            ))}
          </div>
        )}
        {CONFIG[tab] && <CrudSection cfg={CONFIG[tab]} tab={tab} />}
        {tab === "Site Mode" && <SiteModeSection />}
        {tab === "Media Kit" && <MediaKitSection />}
        {LIST_TABS[tab] && <SimpleList tab={tab} />}
      </section>
    </main>
  );
}
